import type { FC } from 'react';
import { Eye, IndianRupee, Mail, MessageSquare, Stethoscope } from 'lucide-react';
import { defineMessages, useIntl } from 'react-intl';

import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { arr, optStr, str } from '@/app/lib/a2ui/props.ts';

const messages = defineMessages({
  finding: {
    id: 'nothingToBuy.finding',
    defaultMessage: 'What it actually is',
    description: 'Heading above the real cause, where no crop-protection product is needed.',
  },
  whatToDo: {
    id: 'nothingToBuy.whatToDo',
    defaultMessage: 'What to do instead',
    description: 'Section heading above the steps that replace a spray.',
  },
  watchFor: {
    id: 'nothingToBuy.watchFor',
    defaultMessage: 'By then you should see',
    description: 'Section heading above the improvement to look for.',
  },
  emailedTo: {
    id: 'nothingToBuy.emailedTo',
    defaultMessage: 'Plan emailed to {address}',
    description: 'Confirmation that the free plan was emailed to the farmer.',
  },
});

interface Step {
  step: string;
  detail?: string;
}

function readSteps(value: unknown): Step[] {
  return arr(value).flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) {
      return [];
    }
    const record = entry as Record<string, unknown>;
    const step = str(record.step);
    return step ? [{ step, detail: optStr(record.detail) }] : [];
  });
}

export const NothingToBuy: FC<A2uiNodeViewProps> = ({ node }) => {
  const intl = useIntl();
  const heading = str(node.props.heading);
  const cause = str(node.props.cause);
  const why = str(node.props.why);
  const steps = readSteps(node.props.steps);
  const watchFor = str(node.props.watchFor);
  const ifWorse = optStr(node.props.ifWorse);
  const costLine = str(node.props.costLine);
  const emailedTo = optStr(node.props.emailedTo);

  return (
    <div className="w-full">
      <div className="rounded-lg border-2 border-primary/40 bg-card p-5">
        <p className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-caps text-primary">
          <Stethoscope className="size-5 shrink-0" aria-hidden />
          {heading}
        </p>

        <p className="mb-1 text-xs font-semibold uppercase tracking-caps text-muted-foreground">
          {intl.formatMessage(messages.finding)}
        </p>
        <h2
          className="mb-2 text-balance text-2xl font-bold leading-tight tracking-tight text-foreground"
          translate="no"
        >
          {cause}
        </h2>
        <p className="text-base leading-relaxed text-foreground">{why}</p>

        <p className="mt-5 flex items-center gap-2 rounded-md bg-primary px-4 py-3 text-lg font-bold text-primary-foreground">
          <IndianRupee className="size-5 shrink-0" aria-hidden />
          <span className="min-w-0">{costLine}</span>
        </p>
      </div>

      {steps.length > 0 ? (
        <section className="mt-4 rounded-lg border border-border bg-card p-5">
          <p className="mb-3 text-sm font-bold uppercase tracking-caps text-muted-foreground">
            {intl.formatMessage(messages.whatToDo)}
          </p>
          <ol className="space-y-3">
            {steps.map((item, index) => (
              <li key={item.step} className="flex items-start gap-3">
                <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold tabular-nums text-primary">
                  {index + 1}
                </span>
                <span className="min-w-0">
                  <span className="block text-base font-semibold leading-snug text-foreground">
                    {item.step}
                  </span>
                  {item.detail ? (
                    <span className="block text-sm leading-snug text-muted-foreground">
                      {item.detail}
                    </span>
                  ) : null}
                </span>
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      <section className="mt-4 rounded-lg border border-border bg-muted p-5">
        <p className="mb-1.5 flex items-center gap-2 text-sm font-bold uppercase tracking-caps text-muted-foreground">
          <Eye className="size-4 shrink-0" aria-hidden />
          {intl.formatMessage(messages.watchFor)}
        </p>
        <p className="text-base leading-relaxed text-foreground">{watchFor}</p>
        {ifWorse ? (
          <p className="mt-2 flex items-start gap-2 text-sm text-muted-foreground">
            <MessageSquare className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span className="min-w-0">{ifWorse}</span>
          </p>
        ) : null}
      </section>

      {emailedTo ? (
        <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
          <Mail className="size-4 shrink-0" aria-hidden />
          <span className="min-w-0 truncate" translate="no">
            {intl.formatMessage(messages.emailedTo, { address: emailedTo })}
          </span>
        </p>
      ) : null}
    </div>
  );
};
